/**
 * SynapSee Theme
 * Default theme for SynapSee Studio
 * Primary: Red (#E53935), Secondary: Blue (#1E88E5), Accent: Amber (#FFC107)
 */

import { synapseeColors } from '../tokens/colors.js';
import { spacing, zIndex } from '../tokens/spacing.js';
import { typography } from '../tokens/typography.js';
import { borderRadius, shadows, transitions } from '../tokens/effects.js';

export const synapseeTheme = {
  name: 'synapsee',
  label: 'SynapSee 2025',
  description: 'Default SynapSee Studio theme with vibrant red and blue',
  isDark: false,

  // Color palette
  colors: {
    primary: synapseeColors.primary,
    primaryLight: synapseeColors.primaryLight,
    primaryDark: synapseeColors.primaryDark,
    primaryHover: synapseeColors.primaryDark,
    primaryOpacity: synapseeColors.primaryOpacity20,

    secondary: synapseeColors.secondary,
    secondaryLight: synapseeColors.secondaryLight,
    secondaryDark: synapseeColors.secondaryDark,
    secondaryOpacity: synapseeColors.secondaryOpacity20,

    tertiary: synapseeColors.tertiary,
    tertiaryLight: synapseeColors.tertiaryLight,
    tertiaryDark: synapseeColors.tertiaryDark,
    tertiaryOpacity: synapseeColors.tertiaryOpacity20,

    accent: synapseeColors.accent,
    accentLight: synapseeColors.accentLight,
    accentDark: synapseeColors.accentDark,
    accentOpacity: synapseeColors.accentOpacity20,

    // Semantic colors
    error: synapseeColors.error,
    errorLight: synapseeColors.errorLight,
    success: synapseeColors.success,
    successLight: synapseeColors.successLight,
    warning: synapseeColors.warning,
    warningLight: synapseeColors.warningLight,
    info: synapseeColors.info,
    infoLight: synapseeColors.infoLight,

    // Neutrals
    background: synapseeColors.backgroundLight,
    backgroundAlt: synapseeColors.backgroundDark,
    backgroundDark: synapseeColors.backgroundDark,
    backgroundNeutral: '#F8F9FA',
    surface: synapseeColors.surfaceLight,
    surfaceElevated: synapseeColors.surfaceElevated,

    // Text
    textPrimary: synapseeColors.darkGray900,
    textSecondary: synapseeColors.mediumGray600,
    textTertiary: synapseeColors.mediumGray500,
    textInverse: synapseeColors.white,

    // Borders
    borderDefault: synapseeColors.lightGray300,
    borderSubtle: synapseeColors.lightGray200,
    borderInput: synapseeColors.borderInput,
  },

  // Spacing
  spacing,

  // Z-index
  zIndex,

  // Typography
  typography,

  // Border radius
  borderRadius,

  // Shadows
  shadows,

  // Transitions
  transitions,

  // Component-specific tokens
  components: {
    button: {
      paddingX: spacing.base,
      paddingY: '10px',
      borderRadius: borderRadius.md,
      transition: transitions.default,
      fontSize: typography.fontSize.body,
      fontWeight: typography.fontWeight.semibold,
      // SYN-2738 (A9): fundos/texto do DSButton por tema (--button-*).
      primaryBg: synapseeColors.primary,
      primaryBgHover: synapseeColors.primaryDark,
      // C12 (2026-10-05): texto do botão ghost/de texto. Aqui = a cor primária de sempre.
      ghostText: synapseeColors.primary,
      secondaryBg: synapseeColors.secondary,
      secondaryBgHover: synapseeColors.secondaryDark,
      accentText: synapseeColors.darkGray900,
    },
    card: {
      padding: spacing.xl,
      borderRadius: borderRadius.lg,
      backgroundColor: synapseeColors.surfaceLight,
      boxShadow: shadows.base,
    },
    input: {
      paddingX: spacing.base,
      paddingY: '10px',
      borderRadius: borderRadius.md,
      borderColor: synapseeColors.lightGray300,
      focusBorderColor: synapseeColors.primary,
      fontSize: typography.fontSize.body,
    },
    chip: {
      paddingX: spacing.md,
      paddingY: spacing.xs,
      borderRadius: borderRadius.full,
      fontSize: typography.fontSize.labelSmall,
      fontWeight: typography.fontWeight.medium,
    },
    badge: {
      paddingX: spacing.sm,
      paddingY: spacing.xs,
      borderRadius: borderRadius.sm,
      fontSize: typography.fontSize.labelSmall,
      fontWeight: typography.fontWeight.bold,
    },
    sidebar: {
      backgroundColor: synapseeColors.backgroundLight,
      iconSelected: synapseeColors.primary,
      iconDefault: synapseeColors.mediumGray600,
      iconDisabled: synapseeColors.lightGray400,
      iconStatic: synapseeColors.darkGray800,
      divider: synapseeColors.lightGray300,
    },
  },

  // Typography styles (hero, featured, lead text)
  typographyStyles: {
    heroTitle: typography.styles.heroTitle,
    heroSubtitle: typography.styles.heroSubtitle,
    featuredText: typography.styles.featuredText,
    leadText: typography.styles.leadText,
  },

  // Vuetify theme configuration
  vuetifyTheme: {
    dark: false,
    colors: {
      primary: synapseeColors.primary,
      secondary: synapseeColors.secondary,
      accent: synapseeColors.accent,
      error: synapseeColors.error,
      warning: synapseeColors.warning,
      info: synapseeColors.info,
      success: synapseeColors.success,
      background: synapseeColors.backgroundLight,
      surface: synapseeColors.surfaceLight,
    },
  },
};
